'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Review {
  id: number;
  name: string;
  location: string;
  image: string;
  alt: string;
  rating: number;
  service: string;
  testimonial: string;
  date: string;
  moveDetails: string;
  photos?: {url: string;alt: string;}[];
  helpful: number;
}

const ReviewsGrid = () => {
  const reviews: Review[] = [
  {
    id: 1,
    name: 'Ahmed Al-Rashid',
    location: 'Farwaniya, Kuwait',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b4710b43-1763294093140.png',
    alt: 'Middle-aged Arab man with short black hair wearing white traditional dress smiling confidently',
    rating: 5,
    service: 'Residential Move',
    testimonial: 'Almanara made our villa move completely stress-free. The team was professional, careful with our belongings, and finished ahead of schedule. They handled our antique furniture with exceptional care. Highly recommended!',
    date: 'December 2025',
    moveDetails: '4-bedroom villa • 2 trucks • 8 hours',
    photos: [
    { url: "https://img.rocket.new/generatedImages/rocket_gen_img_1821c909d-1768177040839.png", alt: 'Professional movers loading furniture into Almanara truck' },
    { url: 'https://images.unsplash.com/photo-1590451669839-60ac20fba347', alt: 'Neatly packed moving boxes in living room' }],

    helpful: 42
  },
  {
    id: 2,
    name: 'Sarah Mitchell',
    location: 'Salmiya, Kuwait',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1fc23a89f-1763301305375.png',
    alt: 'Young woman with blonde hair in casual blue shirt smiling warmly at camera',
    rating: 5,
    service: 'International Move',
    testimonial: 'Moving internationally is challenging, but Almanara\'s team made it seamless. They handled customs paperwork, packing, and delivery with professionalism. Everything arrived safely in perfect condition.',
    date: 'November 2025',
    moveDetails: 'International relocation • Full packing service • 3 weeks',
    helpful: 38
  },
  {
    id: 3,
    name: 'Khalid Al-Mutairi',
    location: 'Kuwait City',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_16679c0dc-1763293361742.png',
    alt: 'Professional Arab businessman in navy suit with trimmed beard looking confident',
    rating: 5,
    service: 'Commercial Move',
    testimonial: 'We relocated our entire office with Almanara. Zero downtime, professional service, and excellent value. They coordinated everything perfectly and our business was operational the next day.',
    date: 'January 2026',
    moveDetails: 'Office relocation • 50 workstations • Weekend move',
    photos: [
    { url: "https://img.rocket.new/generatedImages/rocket_gen_img_16da79096-1768177040847.png", alt: 'Office computers and desks being carefully moved' }],

    helpful: 56
  },
  {
    id: 4,
    name: 'Fatima Hassan',
    location: 'Hawally, Kuwait',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_16f1d13d5-1763294375575.png',
    alt: 'Young Arab woman with long dark hair wearing professional attire smiling brightly',
    rating: 5,
    service: 'Apartment Move',
    testimonial: 'The packing service was exceptional! Every item arrived safely at my new apartment. The team was respectful, efficient, and very professional. Best moving experience I\'ve ever had.',
    date: 'October 2025',
    moveDetails: '2-bedroom apartment • Full packing • Same day',
    helpful: 31
  },
  {
    id: 5,
    name: 'Mohammed Al-Sabah',
    location: 'Jabriya, Kuwait',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17972208b-1763293714337.png",
    alt: 'Young Arab man with beard wearing casual shirt smiling confidently',
    rating: 5,
    service: 'Storage Service',
    testimonial: 'Needed temporary storage during renovation. Almanara provided secure climate-controlled storage and handled everything professionally. Retrieved items in perfect condition.',
    date: 'September 2025',
    moveDetails: 'Storage • 3 months • Climate controlled',
    helpful: 24
  },
  {
    id: 6,
    name: 'Jennifer Thompson',
    location: 'Mangaf, Kuwait',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c36b389a-1763293506589.png",
    alt: 'Woman with brown hair wearing business attire smiling professionally',
    rating: 4,
    service: 'Residential Move',
    testimonial: 'Great service overall. The team was punctual and careful with our belongings. One small delay due to traffic but they communicated well and made up the time.',
    date: 'August 2025',
    moveDetails: '3-bedroom house • Partial packing • 6 hours',
    helpful: 19
  }];


  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto space-y-6">
          {reviews.map((review) =>
          <div
            key={review.id}
            className="bg-card rounded-xl p-6 lg:p-8 shadow-soft hover:shadow-elevated transition-all duration-base">

              <div className="flex items-start space-x-4 mb-4">
                <div className="relative w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                  <AppImage
                  src={review.image}
                  alt={review.alt}
                  className="w-full h-full object-cover" />

                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-headline font-bold text-lg text-text-primary">{review.name}</h3>
                      <p className="text-sm text-text-secondary font-body">{review.location}</p>
                    </div>
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, index) =>
                    <Icon
                      key={index}
                      name="StarIcon"
                      size={18}
                      variant="solid"
                      className={index < review.rating ? 'text-accent' : 'text-gray-300'} />

                    )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="inline-block px-3 py-1 bg-muted rounded-full text-xs font-semibold text-text-secondary">
                      {review.service}
                    </span>
                    <div className="flex items-center space-x-1 text-success">
                      <Icon name="CheckBadgeIcon" size={16} variant="solid" />
                      <span className="text-xs font-semibold">Verified Customer</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-text-primary font-body leading-relaxed mb-4">
                {review.testimonial}
              </p>
              
              <div className="flex items-center space-x-2 text-sm text-text-secondary font-body mb-4">
                <Icon name="InformationCircleIcon" size={16} variant="outline" />
                <span>{review.moveDetails}</span>
              </div>
              
              {review.photos && review.photos.length > 0 &&
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {review.photos.map((photo, index) =>
              <div key={index} className="relative aspect-video rounded-lg overflow-hidden">
                      <AppImage
                  src={photo.url}
                  alt={photo.alt}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-base" />

                    </div>
              )}
                </div>
            }
              
              <div className="flex items-center justify-between pt-4 border-t border-border">
                <span className="text-sm text-text-secondary font-body">{review.date}</span>
                <button className="flex items-center space-x-2 text-text-secondary hover:text-primary transition-colors duration-base">
                  <Icon name="HandThumbUpIcon" size={18} variant="outline" />
                  <span className="text-sm font-body font-medium">Helpful ({review.helpful})</span>
                </button>
              </div>
            </div>
          )}
        </div>
        
        <div className="text-center mt-12">
          <button className="inline-flex items-center space-x-2 px-8 py-4 bg-primary text-primary-foreground rounded-lg hover:bg-opacity-90 transition-all duration-base font-body font-semibold shadow-soft">
            <span>Load More Reviews</span>
            <Icon name="ChevronDownIcon" size={20} variant="outline" />
          </button>
        </div>
      </div>
    </section>);

};

export default ReviewsGrid;